import { PrivyProvider } from "@privy-io/react-auth";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import { FrontDoor } from "./components/FrontDoor";
import { config, missingConfig } from "./config";
import { SessionProvider } from "./session/SessionContext";
import "./styles.css";

const missing = missingConfig();

function ConfigError({ names }: { names: string[] }) {
  return (
    <FrontDoor title="Console is not configured">
      <p className="subtle">
        These build-time variables are missing, so the console cannot reach the
        API or start a login. Set them and rebuild.
      </p>
      <div className="mono-block">{names.join(", ")}</div>
    </FrontDoor>
  );
}

// iOS Safari only applies `:active` on a page that listens for touches. The
// press states in styles.css are the whole of the console's touch feedback,
// so without this a tap shows nothing until the next screen arrives.
document.addEventListener("touchstart", () => {}, { passive: true });

const root = createRoot(document.getElementById("root") as HTMLElement);

root.render(
  <StrictMode>
    {missing.length > 0 ? (
      <ConfigError names={missing} />
    ) : (
      <PrivyProvider
        appId={config.privyAppId}
        config={{
          // What the mobile app offers, MINUS Apple, so an operator signs in
          // with the identity they already have. It matters beyond
          // convenience: this is one Privy app (ADR 0013), and a grant
          // resolves an EMAIL, which Privy attaches to the account whichever
          // way they came in.
          //
          // Apple is absent because on the web it cannot work here. Privy
          // holds ONE Apple Client ID per app, and iOS needs it to be the
          // bundle id: `@privy-io/expo` does the NATIVE Sign in with Apple by
          // default, whose token audience is `com.nerona.mobile`. Apple's web
          // flow only accepts a Services ID, so this button reached
          // appleid.apple.com and came back `invalid_request: Invalid client
          // id or web redirect url`. Restoring it needs Privy support (Apple
          // credentials cannot be changed from the dashboard once in use),
          // not a change here.
          loginMethods: ["email", "google"],
          appearance: {
            theme: "light",
            // --accent, spelled out: this is a JS prop, not a stylesheet.
            accentColor: "#8c71f6",
            logo: `${import.meta.env.BASE_URL}nerona-logo.svg`,
          },
        }}
      >
        {/* Without the trailing slash Vite puts on BASE_URL: the router only
            matches a basename the URL starts with exactly, so "/x/" rendered
            nothing at "/x". "/x" matches both. */}
        <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <SessionProvider>
            <App />
          </SessionProvider>
        </BrowserRouter>
      </PrivyProvider>
    )}
  </StrictMode>,
);
